import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { apiClient } from '@/shared/api/apiClient'
import type {
  ProductLawApplicability,
} from '@/shared/hooks/api/product'
import type { ComplianceFileStatus } from '@/shared/hooks/api/rohsComplianceFiles'
import { OPEN_ROHS_PARTS_QUERY_KEY } from '@/shared/hooks/api/openRohsParts'

export const CATALOG_SUPPLIERS_QUERY_KEY = 'catalogSuppliers' as const
export const CATALOG_SUPPLIER_DETAIL_QUERY_KEY =
  'catalogSupplierDetail' as const
export const CATALOG_COMPONENTS_QUERY_KEY = 'catalogComponents' as const
export const CATALOG_COMPONENT_DETAIL_QUERY_KEY =
  'catalogComponentDetail' as const
export const PRODUCT_COMPONENTS_QUERY_KEY = 'productComponents' as const
export const PRODUCT_CATALOG_SUPPLIER_QUERY_KEY =
  'productCatalogSupplier' as const

export const CATALOG_SUPPLIER_ONBOARDING_STATUSES = [
  'INVITE_SENT',
  'INVITE_OPENED',
  'ACKNOWLEDGED',
] as const

export type CatalogSupplierOnboardingStatus =
  (typeof CATALOG_SUPPLIER_ONBOARDING_STATUSES)[number]

export type CatalogSupplier = {
  id: string
  organizationId: string
  name: string
  address: string | null
  contactName: string | null
  contactEmail: string | null
  magicLinkToken: string
  magicLinkUrl: string
  magicLinkFirstOpenedAt: string | null
  portalProfileCompletedAt: string | null
  requestAcknowledgedAt: string | null
  requestAcknowledgedByName: string | null
  magicLinkLastRotatedAt: string | null
  magicLinkRotateCount: number
  onboardingStatus: CatalogSupplierOnboardingStatus
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export type CatalogSupplierDetailProduct = {
  id: string
  name: string | null
  sku: string | null
  productModel: string | null
}

export type CatalogSupplierDetailComponent = {
  id: string
  sku: string
  name: string | null
  linkedProductCount: number
}

export type CatalogSupplierDetail = {
  id: string
  organizationId: string
  name: string
  address: string | null
  contactName: string | null
  contactEmail: string | null
  magicLinkUrl: string
  magicLinkFirstOpenedAt: string | null
  portalProfileCompletedAt: string | null
  requestAcknowledgedAt: string | null
  requestAcknowledgedByName: string | null
  magicLinkLastRotatedAt: string | null
  magicLinkRotateCount: number
  onboardingStatus: CatalogSupplierOnboardingStatus
  products: CatalogSupplierDetailProduct[]
  components: CatalogSupplierDetailComponent[]
}

export type UpdateCatalogSupplierPayload = {
  name?: string
  address?: string | null
  contactName?: string | null
  contactEmail?: string | null
}

export type CatalogComponentSupplier = {
  id: string
  name: string
}

export type CatalogComponentRohsFileListSummary = {
  status: ComplianceFileStatus
  completedAt: string | null
  filledSlotCount: number
  totalSlotCount: number
}

export type CatalogComponent = {
  id: string
  organizationId: string
  supplierId: string
  sku: string
  name: string | null
  description: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  supplier: CatalogComponentSupplier
  linkedProductCount: number
  rohsFile: CatalogComponentRohsFileListSummary | null
}

export type CreateCatalogSupplierPayload = {
  name: string
  contactName?: string | null
  contactEmail?: string | null
}

export type CreateCatalogComponentPayload = {
  supplierId: string
  sku: string
  name?: string | null
  description?: string | null
}

export type ProductComponentLink = {
  productId: string
  componentId: string
  complianceActor: 'OEM' | 'SUPPLIER'
  createdAt: string
  updatedAt: string
  component: Omit<CatalogComponent, 'supplier'> & {
    supplier: {
      id: string
      name: string
      magicLinkUrl: string
    }
  }
}

export type LinkProductComponentPayload = {
  componentId: string
  complianceActor?: 'OEM' | 'SUPPLIER'
}

export type ProductCatalogSupplierSummary = {
  id: string
  name: string
  contactName: string | null
  contactEmail: string | null
}

export type ProductCatalogSupplierResponse = {
  catalogSupplier: ProductCatalogSupplierSummary | null
}

export type SetProductCatalogSupplierPayload = {
  catalogSupplierId: string
}

export type ProductBomImportResult = {
  createdSuppliers: number
  createdComponents: number
  updatedComponents: number
  linked: number
  failed: Array<{ row: number; sku?: string; reason: string }>
}

export type ProductBomColumnMapping = {
  skuColumn: number
  supplierColumn: number
  nameColumn?: number | null
}

export type ProductBomImportMappingRequired = {
  needsColumnMapping: true
  columns: Array<{ index: number; header: string }>
  sampleRows: string[][]
  suggestedMapping: {
    skuColumn: number | null
    supplierColumn: number | null
    nameColumn: number | null
  }
}

export type ProductBomImportPreview = {
  columns: Array<{ index: number; header: string }>
  suggestedMapping: {
    skuColumn: number | null
    supplierColumn: number | null
    nameColumn: number | null
  }
  rows: Array<{ rowNumber: number; cells: string[] }>
}

export type ProductBomImportInput = {
  file: File
  mapping: ProductBomColumnMapping
  importRowNumbers: number[]
}

export type CatalogComponentLinkedProduct = {
  id: string
  name: string | null
  sku: string | null
  complianceActor: 'OEM' | 'SUPPLIER'
  machinery: ProductLawApplicability | null
  rohs: ProductLawApplicability | null
}

export type CatalogComponentRohsFileSummary = {
  status: ComplianceFileStatus
  completedAt: string | null
  completedByActor: 'OEM' | 'SUPPLIER' | null
  filledSlotCount: number
  totalSlotCount: number
  provisionedBecause: Array<{ id: string; name: string }>
}

export type CatalogComponentDetail = {
  id: string
  sku: string
  name: string | null
  description: string | null
  supplier: {
    id: string
    name: string
    magicLinkUrl: string
  }
  products: CatalogComponentLinkedProduct[]
  rohsFile: CatalogComponentRohsFileSummary | null
}

export function useListCatalogSuppliers() {
  return useQuery<CatalogSupplier[], Error>({
    queryKey: [CATALOG_SUPPLIERS_QUERY_KEY, 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CatalogSupplier[]>(
        '/v1/catalog/suppliers'
      )
      return response.data
    },
  })
}

export function useGetCatalogSupplier(
  supplierId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<CatalogSupplierDetail, Error>({
    queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY, supplierId],
    queryFn: async () => {
      const response = await apiClient.get<CatalogSupplierDetail>(
        `/v1/catalog/suppliers/${supplierId}`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) &&
      supplierId != null &&
      supplierId.length > 0,
  })
}

export function useCreateCatalogSupplier() {
  const queryClient = useQueryClient()

  return useMutation<CatalogSupplier, Error, CreateCatalogSupplierPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<CatalogSupplier>(
        '/v1/catalog/suppliers',
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIERS_QUERY_KEY],
      })
    },
  })
}

export function useUpdateCatalogSupplier(supplierId: string) {
  const queryClient = useQueryClient()

  return useMutation<CatalogSupplier, Error, UpdateCatalogSupplierPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.patch<CatalogSupplier>(
        `/v1/catalog/suppliers/${supplierId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIERS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY, supplierId],
      })
    },
  })
}

export function useDeleteCatalogSupplier() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (supplierId) => {
      await apiClient.delete(`/v1/catalog/suppliers/${supplierId}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIERS_QUERY_KEY],
      })
    },
  })
}

export function useRotateCatalogSupplierMagicLink(supplierId: string) {
  const queryClient = useQueryClient()

  return useMutation<CatalogSupplier, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<CatalogSupplier>(
        `/v1/catalog/suppliers/${supplierId}/rotate-magic-link`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIERS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY, supplierId],
      })
    },
  })
}

export function useListCatalogComponents() {
  return useQuery<CatalogComponent[], Error>({
    queryKey: [CATALOG_COMPONENTS_QUERY_KEY, 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CatalogComponent[]>(
        '/v1/catalog/components'
      )
      return response.data
    },
  })
}

export function useGetCatalogComponent(
  componentId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<CatalogComponentDetail, Error>({
    queryKey: [CATALOG_COMPONENT_DETAIL_QUERY_KEY, componentId],
    queryFn: async () => {
      const response = await apiClient.get<CatalogComponentDetail>(
        `/v1/catalog/components/${componentId}`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) &&
      componentId != null &&
      componentId.length > 0,
  })
}

export function useCreateCatalogComponent() {
  const queryClient = useQueryClient()

  return useMutation<CatalogComponent, Error, CreateCatalogComponentPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<CatalogComponent>(
        '/v1/catalog/components',
        payload
      )
      return response.data
    },
    onSuccess: (_data, payload) => {
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_COMPONENTS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY, payload.supplierId],
      })
    },
  })
}

export function useGetProductCatalogSupplier(productId: string | undefined) {
  return useQuery<ProductCatalogSupplierResponse, Error>({
    queryKey: [PRODUCT_CATALOG_SUPPLIER_QUERY_KEY, productId],
    queryFn: async () => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.get<ProductCatalogSupplierResponse>(
        `/v2/products/${productId}/supplier`
      )
      return response.data
    },
    enabled: productId != null && productId.length > 0,
  })
}

export function useSetProductCatalogSupplier(productId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<
    ProductCatalogSupplierResponse,
    Error,
    SetProductCatalogSupplierPayload
  >({
    mutationFn: async (payload) => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.put<ProductCatalogSupplierResponse>(
        `/v2/products/${productId}/supplier`,
        payload
      )
      return response.data
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_CATALOG_SUPPLIER_QUERY_KEY, productId],
      })
      const supplierId = data.catalogSupplier?.id
      if (supplierId != null) {
        void queryClient.invalidateQueries({
          queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY, supplierId],
        })
      }
    },
  })
}

export function useClearProductCatalogSupplier(productId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<ProductCatalogSupplierResponse, Error, void>({
    mutationFn: async () => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.delete<ProductCatalogSupplierResponse>(
        `/v2/products/${productId}/supplier`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_CATALOG_SUPPLIER_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY],
      })
    },
  })
}

export function useListProductComponents(productId: string | undefined) {
  return useQuery<ProductComponentLink[], Error>({
    queryKey: [PRODUCT_COMPONENTS_QUERY_KEY, productId],
    queryFn: async () => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.get<ProductComponentLink[]>(
        `/v2/products/${productId}/components`
      )
      return response.data
    },
    enabled: productId != null && productId.length > 0,
  })
}

export function useLinkProductComponent(productId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<ProductComponentLink, Error, LinkProductComponentPayload>({
    mutationFn: async (payload) => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.post<ProductComponentLink>(
        `/v2/products/${productId}/components`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPONENTS_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_COMPONENTS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_COMPONENT_DETAIL_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY],
      })
    },
  })
}

function invalidateProductPartsQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string | undefined
) {
  void queryClient.invalidateQueries({
    queryKey: [PRODUCT_COMPONENTS_QUERY_KEY, productId],
  })
  void queryClient.invalidateQueries({
    queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
  })
  void queryClient.invalidateQueries({
    queryKey: [CATALOG_COMPONENTS_QUERY_KEY],
  })
  void queryClient.invalidateQueries({
    queryKey: [CATALOG_COMPONENT_DETAIL_QUERY_KEY],
  })
  void queryClient.invalidateQueries({
    queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY],
  })
}

export function useUnlinkProductComponent(productId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { componentId: string }>({
    mutationFn: async ({ componentId }) => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      await apiClient.delete(
        `/v2/products/${productId}/components/${componentId}`
      )
    },
    onSuccess: () => {
      invalidateProductPartsQueries(queryClient, productId)
    },
  })
}

export type ClearProductPartsResult = {
  removed: number
}

export function useClearProductParts(productId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<ClearProductPartsResult, Error, void>({
    mutationFn: async () => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }
      const response = await apiClient.delete<ClearProductPartsResult>(
        `/v2/products/${productId}/components`
      )
      return response.data
    },
    onSuccess: () => {
      invalidateProductPartsQueries(queryClient, productId)
    },
  })
}

export function isProductBomImportMappingRequired(
  error: unknown
): error is { response: { data: ProductBomImportMappingRequired } } {
  if (!isAxiosError(error) || error.response?.status !== 400) {
    return false
  }

  const data = error.response.data
  if (data == null || typeof data !== 'object') {
    return false
  }

  return (data as { needsColumnMapping?: unknown }).needsColumnMapping === true
}

export function usePreviewProductBomImport(productId: string | undefined) {
  return useMutation<ProductBomImportPreview, Error, { file: File }>({
    mutationFn: async ({ file }) => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }

      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post<ProductBomImportPreview>(
        `/v2/products/${productId}/components/import/preview`,
        formData
      )
      return response.data
    },
  })
}

export function useImportProductBom(productId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation<ProductBomImportResult, Error, ProductBomImportInput>({
    mutationFn: async ({ file, mapping, importRowNumbers }) => {
      if (productId == null || productId.length === 0) {
        throw new Error('Product ID is required')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('skuColumn', String(mapping.skuColumn))
      formData.append('supplierColumn', String(mapping.supplierColumn))
      if (mapping.nameColumn != null) {
        formData.append('nameColumn', String(mapping.nameColumn))
      }
      formData.append('importRowNumbers', importRowNumbers.join(','))

      const response = await apiClient.post<ProductBomImportResult>(
        `/v2/products/${productId}/components/import`,
        formData
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPONENTS_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_COMPONENTS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIERS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        queryKey: [CATALOG_SUPPLIER_DETAIL_QUERY_KEY],
      })
    },
  })
}
